(() => {
  if (customElements.get('nm-product')) return;
  class NmProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.variants = JSON.parse(this.querySelector('[data-nm-variants]').textContent);
      this.form = this.querySelector('[data-nm-product-form]');
      this.inputs = [...this.querySelectorAll('[data-nm-option]')];
      this.variantInput = this.querySelector('[data-nm-variant-input]');
      this.addButton = this.querySelector('[data-nm-product-add]');
      this.sticky = this.querySelector('[data-nm-sticky]');
      this.error = this.querySelector('[data-nm-product-error]');
      this.stock = this.querySelector('[data-nm-stock]');
      this.sizeIndex = Number(this.dataset.sizeIndex);
      this.colourIndex = Number(this.dataset.colourIndex);
      this.activeMedia = this.querySelector('[data-nm-media].is-active');
      this.initialVariant = this.variants.find((variant) => String(variant.id) === this.dataset.initialVariant) || this.variants[0];
      this.selected = this.selectionFromUrl(this.dataset.explicitVariant);
      this.inputs.forEach((input) => {
        input.checked = this.selected[Number(input.dataset.nmOption)] === input.value;
        input.addEventListener('change', () => this.changeOption(input), { signal: this.signal });
      });
      this.form.addEventListener('submit', (event) => this.addSingle(event), { signal: this.signal });
      this.querySelector('[data-nm-sticky-add]').addEventListener('click', () => this.form.requestSubmit(this.addButton), { signal: this.signal });
      this.querySelectorAll('[data-nm-thumbnail]').forEach((button) => button.addEventListener('click', () => this.showMedia(button.dataset.nmThumbnail), { signal: this.signal }));
      this.setupGalleryNavigation();
      this.querySelector('[data-nm-zoom]')?.addEventListener('click', (event) => this.openZoom(event.currentTarget), { signal: this.signal });
      this.querySelector('[data-nm-guide-open]')?.addEventListener('click', (event) => {
        event.preventDefault();
        this.openDialog(this.querySelector('[data-nm-guide-dialog]'), event.currentTarget);
      }, { signal: this.signal });
      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.querySelector('[data-nm-dialog-close]').addEventListener('click', () => dialog.close(), { signal: this.signal });
        dialog.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        }, { signal: this.signal });
        dialog.addEventListener('close', () => {
          this.dialogOpener?.focus({ preventScroll: true });
          document.documentElement.classList.remove('nm-product-dialog-open');
        }, { signal: this.signal });
      });
      this.querySelectorAll('[data-nm-reviews-link]').forEach((link) => link.addEventListener('click', (event) => {
        const target = this.querySelector('#looxReviews, .loox-reviews-default, .loox-dynamic-section') || document.querySelector('#looxReviews, .loox-reviews-default, .loox-dynamic-section');
        if (target) {
          event.preventDefault();
          target.scrollIntoView({ behavior: this.motion(), block: 'start' });
        }
      }, { signal: this.signal }));
      this.bundleForm = this.querySelector('[data-nm-kaching-form]');
      this.bundleForm?.addEventListener('click', (event) => {
        if (event.target.closest?.('[data-nm-bundle-add]')) this.addBundle(event);
      }, { capture: true, signal: this.signal });
      this.bundleForm?.addEventListener('submit', (event) => this.addBundle(event), { capture: true, signal: this.signal });
      this.querySelector('[data-nm-restock]')?.addEventListener('toggle', (event) => {
        if (event.target.open) document.dispatchEvent(new CustomEvent('aq:klaviyo-embed-visible', { detail: { element: event.target } }));
      }, { signal: this.signal });
      this.classList.add('is-ready');
      this.update(false);
      this.loadPairs();
      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }, { threshold: 0 });
      this.observer.observe(this.addButton);
      window.addEventListener('popstate', () => this.restoreUrlVariant(), { signal: this.signal });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.initialized = false;
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
    variantMatches(variant, selection, ignoreIndex = -1) {
      return selection.every((value, index) => index === ignoreIndex || !value || variant.options[index] === value);
    }
    updateStock() {
      if (!this.stock) return;
      const threshold = Number(this.stock.dataset.nmStockThreshold) || 10;
      const variant = this.variant;
      const visible = Boolean(variant) && variant.tracked === true && Number(variant.inventory) > 0 && Number(variant.inventory) <= threshold;
      this.stock.hidden = !visible;
      if (!visible) return;
      const template = this.stock.dataset.nmStockText || 'Only [count] left in stock';
      this.stock.querySelector('[data-nm-stock-label]').textContent = template.replace('[count]', String(variant.inventory));
    }
    changeOption(input) {
      const index = Number(input.dataset.nmOption);
      this.selected[index] = input.value;
      if (index !== this.sizeIndex && this.sizeIndex >= 0 && this.selected[this.sizeIndex]) {
        const matching = this.variants.some((variant) => variant.available && this.variantMatches(variant, this.selected));
        if (!matching) this.selected[this.sizeIndex] = null;
      }
      this.error.hidden = true;
      this.update(true);
    }
    update(updateUrl) {
      const complete = this.selected.every(Boolean);
      this.variant = complete ? this.variants.find((variant) => this.variantMatches(variant, this.selected)) : null;
      const displayVariant = this.variant || this.variants.find((variant) => variant.available && this.variantMatches(variant, this.selected)) || this.variants.find((variant) => this.variantMatches(variant, this.selected)) || this.initialVariant;
      this.inputs.forEach((input) => {
        const index = Number(input.dataset.nmOption);
        const selection = [...this.selected];
        selection[index] = input.value;
        if (index === this.colourIndex && this.sizeIndex >= 0) selection[this.sizeIndex] = null;
        const available = this.variants.some((variant) => variant.available && this.variantMatches(variant, selection));
        input.disabled = !available;
        input.checked = input.value === this.selected[index];
        input.closest('.nm-product__choice').classList.toggle('is-unavailable', !available);
        input.setAttribute('aria-label', available ? input.value : `${input.value} — ${this.dataset.soldoutLabel}`);
      });
      this.querySelectorAll('[data-nm-option-label]').forEach((label) => {
        const index = Number(label.dataset.nmOptionLabel);
        label.textContent = this.selected[index] || this.dataset.selectLabel;
      });
      this.querySelectorAll('[data-nm-colour-label]').forEach((label) => { label.textContent = this.selected[this.colourIndex] || ''; });
      this.variantInput.disabled = !this.variant;
      this.variantInput.value = this.variant?.id || '';
      if (this.bundleForm) this.bundleForm.querySelector('[data-nm-bundle-variant]').value = displayVariant.id;
      this.querySelector('[data-nm-price]').textContent = displayVariant.priceFormatted;
      const compare = this.querySelector('[data-nm-compare]');
      compare.hidden = displayVariant.comparePrice <= displayVariant.price;
      compare.textContent = displayVariant.compareFormatted;
      this.querySelector('[data-nm-sticky-price]').textContent = displayVariant.priceFormatted;
      this.querySelector('[data-nm-sticky-selection]').textContent = this.selected.filter(Boolean).filter((value) => value !== 'Default Title').join(' / ');
      const hasAvailable = this.variants.some((variant) => variant.available && this.variantMatches(variant, this.selected));
      this.soldOut = this.variant ? !this.variant.available : !hasAvailable;
      const retailer = this.querySelector('[data-nm-retailer]');
      if (retailer) retailer.hidden = this.sizeIndex < 0 || !this.variants.some((variant) => !variant.available && (this.colourIndex < 0 || variant.options[this.colourIndex] === this.selected[this.colourIndex]));
      const label = this.soldOut ? this.dataset.soldoutLabel : this.variant ? this.dataset.addLabel : this.sizeIndex >= 0 ? this.dataset.chooseLabel : this.dataset.optionsLabel;
      this.querySelector('[data-nm-add-text]').textContent = label;
      this.querySelector('[data-nm-sticky-add]').textContent = label;
      this.addButton.disabled = this.soldOut || Boolean(this.adding);
      this.querySelector('[data-nm-sticky-add]').disabled = this.soldOut || Boolean(this.adding);
      this.updateStock();
      const mediaVariant = displayVariant.featured_media?.id ? displayVariant : this.variants.find((variant) => variant.featured_media?.id && (this.colourIndex >= 0 ? variant.options[this.colourIndex] === this.selected[this.colourIndex] : this.variantMatches(variant, this.selected)));
      if (mediaVariant?.featured_media?.id) this.showMedia(String(mediaVariant.featured_media.id));
      if (updateUrl) {
        const url = new URL(location.href);
        if (this.variant) url.searchParams.set('variant', String(this.variant.id));
        else url.searchParams.delete('variant');
        if (this.colourIndex >= 0 && this.selected[this.colourIndex]) url.searchParams.set('color', this.selected[this.colourIndex]);
        else url.searchParams.delete('color');
        url.searchParams.delete('colour');
        history.replaceState({}, '', url);
      }
      window.ARMATEQBNPL?.update(displayVariant.price, 'product', displayVariant);
      this.dispatchEvent(new CustomEvent('nm:variant:change', { bubbles: true, detail: { variant: displayVariant, selectedVariant: this.variant, sectionId: this.dataset.sectionId } }));
    }
    selectionFromUrl(explicitFallback = '') {
      const params = new URL(location.href).searchParams;
      const id = params.get('variant') || explicitFallback;
      const variant = this.variants.find((item) => String(item.id) === id);
      const selection = [...(variant || this.initialVariant).options];
      if (!variant) {
        const colour = params.get('color') || params.get('colour');
        if (colour && this.colourIndex >= 0) {
          const matching = this.variants.find((item) => item.options[this.colourIndex].toLocaleLowerCase() === colour.toLocaleLowerCase());
          if (matching) selection[this.colourIndex] = matching.options[this.colourIndex];
        }
        if (this.sizeIndex >= 0) selection[this.sizeIndex] = null;
      }
      return selection;
    }
    restoreUrlVariant() {
      this.selected = this.selectionFromUrl();
      this.update(false);
    }
    async loadPairs() {
      const pairs = this.querySelector('[data-nm-pairs]');
      if (!pairs?.dataset.nmPairsUrl || pairs.querySelector('[data-nm-card]')) return;
      try {
        const response = await fetch(pairs.dataset.nmPairsUrl, { signal: this.signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const recommendations = html.querySelector('[data-nm-pairs]');
        if (!recommendations?.querySelector('[data-nm-card]') || !this.isConnected) return;
        pairs.replaceChildren(...recommendations.childNodes);
        pairs.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') pairs.hidden = true;
      }
    }
    galleryThumbnails() {
      return [...this.querySelectorAll('[data-nm-thumbnail]')].filter((button) => !button.hidden && button.getAttribute('aria-hidden') !== 'true');
    }
    stepMedia(direction, focus = false, fromId = this.activeMedia?.dataset.nmMedia) {
      const buttons = this.galleryThumbnails();
      if (buttons.length < 2) return;
      const current = buttons.findIndex((button) => button.dataset.nmThumbnail === fromId);
      const next = buttons[(current + direction + buttons.length) % buttons.length];
      this.showMedia(next.dataset.nmThumbnail);
      if (focus) next.focus({ preventScroll: true });
    }
    setupGalleryNavigation() {
      this.querySelectorAll('[data-nm-gallery-step]').forEach((button) => button.addEventListener('click', () => this.stepMedia(Number(button.dataset.nmGalleryStep)), { signal: this.signal }));
      this.querySelector('.nm-product__thumbnails')?.addEventListener('keydown', (event) => {
        const thumbnail = event.target.closest('[data-nm-thumbnail]');
        if (!thumbnail) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.stepMedia(event.key === 'ArrowLeft' ? -1 : 1, true, thumbnail.dataset.nmThumbnail);
        } else if (event.key === 'Home' || event.key === 'End') {
          const buttons = this.galleryThumbnails();
          const button = event.key === 'Home' ? buttons[0] : buttons.at(-1);
          if (!button) return;
          event.preventDefault();
          this.showMedia(button.dataset.nmThumbnail);
          button.focus({ preventScroll: true });
        }
      }, { signal: this.signal });
      const stage = this.querySelector('.nm-product__stage');
      if (!stage) return;
      stage.addEventListener('pointerdown', (event) => {
        this.gallerySwipe = null;
        if (event.pointerType !== 'touch' || !event.isPrimary || !matchMedia('(max-width: 989px)').matches || !event.target.closest('.nm-product__image')) return;
        this.gallerySwipe = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, { signal: this.signal });
      stage.addEventListener('pointerup', (event) => {
        const start = this.gallerySwipe;
        this.gallerySwipe = null;
        if (!start || start.id !== event.pointerId) return;
        const horizontal = event.clientX - start.x;
        const vertical = event.clientY - start.y;
        if (Math.abs(horizontal) >= 50 && Math.abs(horizontal) > Math.abs(vertical) * 1.5) this.stepMedia(horizontal < 0 ? 1 : -1);
      }, { signal: this.signal });
      stage.addEventListener('pointercancel', () => { this.gallerySwipe = null; }, { signal: this.signal });
    }
    showMedia(id) {
      const next = this.querySelector(`[data-nm-media="${id}"]`);
      if (!next) return;
      if (next !== this.activeMedia) {
        this.activeMedia?.querySelectorAll('video').forEach((video) => video.pause());
        this.activeMedia?.querySelectorAll('iframe').forEach((frame) => { frame.src = frame.src; });
      }
      this.querySelectorAll('[data-nm-media]').forEach((media) => {
        media.hidden = media !== next;
        media.classList.toggle('is-active', media === next);
      });
      this.querySelectorAll('[data-nm-thumbnail]').forEach((button) => {
        const active = button.dataset.nmThumbnail === id;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.activeMedia = next;
      this.querySelectorAll('[data-nm-gallery-step]').forEach((button) => { button.hidden = this.galleryThumbnails().length < 2; });
      const zoom = this.querySelector('[data-nm-zoom]');
      if (zoom) zoom.hidden = next.dataset.mediaType !== 'image';
      const image = next.querySelector('img');
      const stickyImage = this.querySelector('[data-nm-sticky-image]');
      if (image && stickyImage) {
        stickyImage.src = image.currentSrc || image.src;
        stickyImage.removeAttribute('srcset');
        stickyImage.alt = image.alt;
      }
    }
    openDialog(dialog, opener) {
      if (!dialog) return;
      this.dialogOpener = opener;
      dialog.showModal();
      document.documentElement.classList.add('nm-product-dialog-open');
      dialog.querySelector('[data-nm-dialog-close]')?.focus();
    }
    openZoom(opener) {
      const current = this.activeMedia?.querySelector('img');
      if (!current) return;
      const image = this.querySelector('[data-nm-zoom-image]');
      image.src = current.src;
      image.alt = current.alt;
      this.openDialog(this.querySelector('[data-nm-zoom-dialog]'), opener);
    }
    async addSingle(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.adding || this.soldOut) return;
      if (!this.variant) {
        this.error.textContent = this.sizeIndex >= 0 ? this.dataset.sizePrompt : this.dataset.optionsLabel;
        this.error.hidden = false;
        const target = this.querySelector('[data-nm-size-group]') || this.querySelector('[data-nm-option-index]');
        target?.scrollIntoView({ behavior: this.motion(), block: 'center' });
        target?.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
        return;
      }
      this.adding = true;
      this.error.hidden = true;
      this.form.setAttribute('aria-busy', 'true');
      this.addButton.disabled = true;
      this.querySelector('[data-nm-sticky-add]').disabled = true;
      this.querySelector('[data-nm-add-text]').textContent = this.dataset.addingLabel;
      try {
        if (window.ARMATEQCart?.addItems) await window.ARMATEQCart.addItems([{ id: this.variant.id, quantity: 1 }]);
        else {
          const response = await fetch(new URL(`${window.Shopify?.routes?.root || '/'}cart/add.js`, location.origin).href, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: this.variant.id, quantity: 1 }] }) });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || this.dataset.errorLabel);
          location.href = `${window.Shopify?.routes?.root || '/'}cart`;
          return;
        }
        this.querySelector('[data-nm-add-text]').textContent = this.dataset.addedLabel;
      } catch (error) {
        this.error.textContent = error.message || this.dataset.errorLabel;
        this.error.hidden = false;
      } finally {
        this.adding = false;
        this.form.removeAttribute('aria-busy');
        this.update(false);
      }
    }
    async addBundle(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.bundleAdding) return;
      const form = this.bundleForm;
      const error = form.querySelector('[data-nm-bundle-error]');
      const button = form.querySelector('[data-nm-bundle-add]');
      const label = button.querySelector('span');
      const original = label.textContent;
      this.bundleAdding = true;
      button.disabled = true;
      form.setAttribute('aria-busy', 'true');
      label.textContent = this.dataset.addingLabel;
      error.hidden = true;
      try {
        const widget = form.querySelector('kaching-bundles-block');
        if (!widget || typeof widget.items !== 'function') throw new Error(this.dataset.bundlePrompt);
        if (typeof widget.validateItemSelection === 'function') {
          if (!widget.validateItemSelection()?.valid) throw new Error(this.dataset.bundlePrompt);
        } else if (typeof widget.isItemSelectionValid !== 'function' || !widget.isItemSelectionValid()) {
          throw new Error(this.dataset.bundlePrompt);
        }
        widget.rotateBundleNonce?.();
        await Promise.resolve();
        const selected = widget.items();
        if (!Array.isArray(selected) || !selected.length) throw new Error(this.dataset.bundlePrompt);
        const items = selected.map((item) => ({ ...item, id: Number(item.id), quantity: Number(item.quantity), properties: { ...(item.properties || {}) } }));
        if (items.some((item) => !Number.isSafeInteger(item.id) || item.id <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity <= 0)) throw new Error(this.dataset.bundlePrompt);
        if (window.ARMATEQCart?.addItems) await window.ARMATEQCart.addItems(items);
        else {
          const response = await fetch(new URL(`${window.Shopify?.routes?.root || '/'}cart/add.js`, location.origin).href, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || this.dataset.errorLabel);
          location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch (failure) {
        error.textContent = failure.message || this.dataset.errorLabel;
        error.hidden = false;
      } finally {
        this.bundleAdding = false;
        button.disabled = false;
        label.textContent = original;
        form.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('nm-product', NmProduct);
})();
